import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import type { ManagedResourceUI } from "@domains/resource/types";
import type { InfraConfigUI } from "@domains/resource/types";
import { DrawerShell, useDrawerDirty } from "@shell/components/DrawerShell";
import { SelectCard } from "@shell/components/SelectCard";

import {
  submitCreateStack,
  extractSubmitErrorMessage,
} from "../../pages/CreateStackPage/submitStack";
import {
  PipelineMode,
  type InfraType,
  type StackTemplate,
  type StackType,
} from "../../types";

import { ResourceAttachment } from "./ResourceAttachment";
import { TemplateSelector } from "./TemplateSelector";
import { useApplicationForm } from "./useApplicationForm";

export interface CreateApplicationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectAlias: string;
  infraConfig: InfraConfigUI | null;
  resources: ManagedResourceUI[];
  onSuccess: (alias: string) => void;
}

export function CreateApplicationDrawer({
  isOpen,
  onClose,
  projectAlias,
  infraConfig,
  resources,
  onSuccess,
}: CreateApplicationDrawerProps): React.ReactElement | null {
  const { setIsDirty } = useDrawerDirty();
  const form = useApplicationForm();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [attachedResourceIds, setAttachedResourceIds] = React.useState<
    number[]
  >([]);

  const handleSubmit = async () => {
    if (!form.validate()) return;
    const payload = form.buildPayload(projectAlias);
    if (!payload) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const created = await submitCreateStack(payload);
      onSuccess(created.alias);
      onClose();
    } catch (err) {
      setSubmitError(extractSubmitErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasInfra = !!infraConfig && !!infraConfig.cloudProvider;
  const pipelineModes = form.PIPELINE_MODES;
  const stackTypes = form.STACK_TYPES;

  return (
    <DrawerShell
      title="New Application"
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Create"
      isSubmitting={isSubmitting}
      isSubmitDisabled={!hasInfra}
      error={submitError}
    >
      <Stack spacing={2.5}>
        {/* Infra blocking banner */}
        {!hasInfra && (
          <Alert severity="warning">
            This project has no infrastructure configured. Set up infrastructure
            first.
          </Alert>
        )}

        {/* Name */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Application Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="my-app"
            value={form.state.name}
            onChange={(e) => {
              form.setField("name", e.target.value);
              setIsDirty(true);
            }}
            onBlur={() => form.onBlur("name")}
            error={!!form.errors.name}
            helperText={form.errors.name}
            disabled={isSubmitting}
          />
        </Box>

        {/* Domain type */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Domain
          </Typography>
          <Stack spacing={1}>
            {stackTypes.map((t) => (
              <SelectCard
                key={t.value}
                label={t.label}
                description={t.description}
                selected={form.state.stackType === t.value}
                onClick={() => {
                  form.setField("stackType", t.value as StackType);
                  setIsDirty(true);
                }}
                disabled={isSubmitting}
              />
            ))}
          </Stack>
          {form.errors.stackType && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, display: "block" }}
            >
              {form.errors.stackType}
            </Typography>
          )}
        </Box>

        {/* Template (only when stack type is selected) */}
        {form.state.stackType && form.state.infraType && (
          <Box sx={{ animation: "fadeIn 200ms ease" }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Template
            </Typography>
            <TemplateSelector
              stackType={form.state.stackType}
              infraType={form.state.infraType as InfraType}
              value={form.state.selectedTemplate}
              onChange={(tmpl: StackTemplate | null) => {
                form.setField("selectedTemplate", tmpl);
                // Initialize services from template
                const templateServices =
                  tmpl?.services?.map((s) => ({
                    name: s.name,
                    type: s.type,
                    cpu: s.resources?.cpu
                      ? parseInt(s.resources.cpu) / 1000
                      : 0.5,
                    memory: s.resources?.memory
                      ? parseInt(s.resources.memory) / 1024
                      : 1,
                    storage: s.storage ? parseInt(s.storage) / 1024 : 10,
                    replicas: 1,
                    internetFacing: s.internetFacing || false,
                  })) ?? [];
                form.setField("configuredServices", templateServices);
                setIsDirty(true);
              }}
            />
          </Box>
        )}

        {/* Deploy mode */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Pipeline Mode
          </Typography>
          <Stack spacing={1}>
            {pipelineModes.map((m) => (
              <SelectCard
                key={m.value}
                label={m.label}
                description={m.description}
                selected={form.state.pipelineMode === m.value}
                onClick={() => {
                  form.setField("pipelineMode", m.value as PipelineMode);
                  setIsDirty(true);
                }}
                disabled={isSubmitting}
              />
            ))}
          </Stack>
        </Box>

        {/* Region */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Region
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="us-east-1"
            value={form.state.region}
            onChange={(e) => {
              form.setField("region", e.target.value);
              setIsDirty(true);
            }}
            onBlur={() => form.onBlur("region")}
            error={!!form.errors.region}
            helperText={form.errors.region}
            disabled={isSubmitting}
          />
        </Box>

        {/* Resource attachment */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Attach Resources
          </Typography>
          <ResourceAttachment
            resources={resources}
            selectedIds={attachedResourceIds}
            onToggle={(id) => {
              setAttachedResourceIds((prev) =>
                prev.includes(id)
                  ? prev.filter((x) => x !== id)
                  : [...prev, id],
              );
              setIsDirty(true);
            }}
          />
        </Box>

        {/* Observability toggle */}
        <Box
          component="button"
          onClick={() => {
            form.setField(
              "enableObservability",
              !form.state.enableObservability,
            );
            setIsDirty(true);
          }}
          disabled={isSubmitting}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            p: 1.25,
            border: "none",
            borderRadius: 1,
            bgcolor: "transparent",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            textAlign: "left",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Observability
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Prometheus + Grafana monitoring
            </Typography>
          </Box>
          {form.state.enableObservability ? (
            <ToggleOnIcon color="primary" />
          ) : (
            <ToggleOffIcon color="disabled" />
          )}
        </Box>

        {/* Advanced */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={500}>
              Advanced
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary">
              Advanced configuration (env vars, resource quotas, security scans)
              is available after creation in the stack settings.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </Stack>
    </DrawerShell>
  );
}
